// flag if the config fields represents a service
// (this is needed as we currently mix services and other settings in our global config)
const serviceConfig = {
  stage: false,
  monolithUrl: false,
  angularUrl: false,
  monolithApiUrl: true,
  organisationService: true,
  iipService: true,
  organisationRegistration: true,
  capacityManagement: true,
  authentication: false,
  surrenderService: true,
  contractManagement: true,
  documentExchange: true,
  gasDataManagement: true,
  snamBidCollector: true,
  transparencyReporting: true,
  directFcfs: true,
  regularFcfs: true,
  hydrogenFcfs: true,
  remitReporting: true,
  lngOperators: false,
  networkPoints: true,
  authorizationService: true,
  lngMarketing: true,
  assignmentService: true,
  transportAuction: true,
  exchangerateService: true,
  secondaryMarketService: true,
  superBundleService: true,
  mapPrototype: true, // ! Implementation for the Map Prototype
  ttpcModulationService: true,
  notificationService: true,
  storageService: true,
} satisfies Record<keyof typeof PRISMA_CONFIG, boolean>;

type ServiceConfig = typeof serviceConfig;
type ServiceConfigKeys = keyof ServiceConfig;

export type Service = {
  [K in ServiceConfigKeys]: ServiceConfig[K] extends true ? K : never;
}[ServiceConfigKeys];

export const services = Object.entries(serviceConfig).reduce(
  (acc, [key, value]) => (value ? [...acc, key as Service] : acc),
  [] as Service[]
);

export const teams = {
  Carma: 'Carma',
  Rupert: 'Rupert',
  Otter: 'Otter',
  Goat: 'Goat',
  Zen: 'Zen',
  Lynx: 'Lynx',
  Paddington: 'Paddington',
} as const;

export type Team = keyof typeof teams;

export const ownershipMapper = {
  assignmentService: teams.Rupert,
  authorizationService: teams.Carma,
  capacityManagement: teams.Carma,
  contractManagement: teams.Carma,
  directFcfs: teams.Lynx,
  documentExchange: teams.Rupert,
  gasDataManagement: teams.Zen,
  iipService: teams.Carma,
  lngMarketing: teams.Paddington,
  monolithApiUrl: teams.Goat,
  networkPoints: teams.Otter,
  organisationRegistration: teams.Rupert,
  organisationService: teams.Rupert,
  regularFcfs: teams.Lynx,
  hydrogenFcfs: teams.Zen,
  remitReporting: teams.Zen,
  snamBidCollector: teams.Goat,
  surrenderService: teams.Carma,
  transparencyReporting: teams.Zen,
  transportAuction: teams.Otter,
  exchangerateService: teams.Rupert,
  secondaryMarketService: teams.Carma,
  superBundleService: teams.Rupert,
  ttpcModulationService: teams.Carma,
  mapPrototype: teams.Carma, // ! Implementation for the Map Prototype
  notificationService: teams.Rupert,
  storageService: teams.Paddington,
} as const satisfies Record<Service, Team>;

/**
 * Team ownership directory patterns for each service.
 * Each service must have at least one pattern defined.
 * Patterns are tested in order, and the first match determines the team ownership.
 */
export const serviceTeamOwnershipPatterns = {
  assignmentService: [
    'src/pages/settings/assignment-settings/',
    'src/pages/shippers/organisations-and-users/',
    'src/pages/shippers/details/',
    'src/pages/operators/components/',
    'src/pages/operators/assignments/',
    'src/pages/operators/operators-tabs',
    'src/pages/operators/operators-tabs.tsx',
  ],
  authorizationService: ['src/pages/settings/permission-groups/'],
  capacityManagement: [
    'src/apis/capacity-management/',
    'src/pages/reporting/balancing-group-allocations/',
    'src/pages/reporting/monthly-statements/',
    'src/pages/reporting/my-transactions/',
    'src/pages/reporting/shipper-transactions/',
    'src/pages/transport/products/drafted/',
    'src/pages/settings/invoices/',
    'src/pages/shippers/invoices/',
    'src/pages/operators/invoices/',
  ],
  contractManagement: [],
  directFcfs: [
    'src/pages/direct-fcfs/',
    'src/pages/reporting/direct-fcfs/',
    'src/pages/settings/direct-fcfs/',
  ],
  documentExchange: [
    'src/pages/shippers/documents/',
    'src/pages/shippers/upload-documents/',
  ],
  gasDataManagement: ['src/pages/gdm/'],
  iipService: ['src/pages/market-messages/'],
  lngMarketing: ['src/pages/lng/', 'src/pages/reporting/lng/'],
  monolithApiUrl: [
    'src/pages/reporting/standard/',
    'src/pages/storage/',
    'src/pages/transport/network-points/',
    'src/pages/transport/bundle-requests/',
    'src/pages/transport/secondary-trading/',
    'src/pages/reporting/secondary-trading/overview/trades/',
    'src/pages/reporting/secondary-trading/overview/trade-proposals/',
    'src/pages/reporting/secondary-trading/trade-proposal/',
    'src/pages/transport/products/components/',
    'src/pages/transport/products/uploaded/',
  ],
  networkPoints: [],
  organisationRegistration: [
    'src/pages/registration/',
    'src/pages/settings/registration/',
  ],
  organisationService: [
    'src/pages/settings/profile/',
    'src/pages/market-information/players/',
    'src/pages/settings/organisation/',
    'src/pages/organisation/',
    'src/pages/market-information/index',
    'src/pages/market-information/index.tsx',
  ],
  regularFcfs: [
    'src/pages/transport/fcfs-booking/',
    'src/pages/reporting/regular-fcfs/',
    'src/pages/settings/regular-fcfs/',
  ],
  remitReporting: [
    'src/pages/reporting/remit-reports/',
    'src/pages/settings/remit-reporting/',
    'src/pages/reporting/secondary-trading/overview/remit-reports/',
    'src/pages/reporting/secondary-trading/external-trades/',
  ],
  snamBidCollector: [
    'src/pages/snam-bid-collector/',
    'src/pages/transport/bid-collector/',
  ],
  surrenderService: ['src/pages/reporting/surrender/'],
  transparencyReporting: ['src/pages/reporting/transparency/'],
  transportAuction: [
    'src/pages/transport/auctions/',
    'src/pages/reporting/auctions/',
    'src/pages/settings/general-transport-settings/',
    'src/pages/settings/auctions/',
  ],
  exchangerateService: ['src/pages/market-information/rates/'],
  secondaryMarketService: [
    'src/pages/transport/secondary-trading/bulletin-board/',
  ],
  superBundleService: [
    'src/pages/transport/super-bundle/',
    'src/pages/reporting/super-bundle/',
  ],
  ttpcModulationService: ['src/pages/modulation-service-auctions/'],
  mapPrototype: ['src/pages/prototype-map/'],
  notificationService: [
    'src/pages/your-timeline-notifications/',
    'src/pages/your-timeline-notifications/components/',
    'src/pages/your-timeline-notifications/details/',
    'src/pages/your-timeline-notifications/overview/',
    'src/pages/your-timeline-notifications/utils/',
    'src/pages/your-timeline-notifications/components/notification-card/',
    'src/pages/your-timeline-notifications/components/notification-card/notification-card.tsx',
    'src/pages/your-timeline-notifications/components/notification-card/notification-card.tsx',
  ],
  hydrogenFcfs: ['src/pages/h2/', 'src/pages/settings/hydrogen-fcfs/'],
  storageService: ['src/pages/storage/'],
} as const satisfies Record<Service, ReadonlyArray<string>>;

/**
 * Team ownership directory patterns for shared infrastructure that doesn't map to a specific service.
 * These patterns are checked after service-specific patterns.
 *
 * Note: Lynx patterns are only needed for paths within delivery team directories
 * (like src/pages/).
 */
export const sharedTeamOwnershipPatterns = {
  [teams.Carma]: [],
  [teams.Rupert]: [],
  [teams.Otter]: ['src/pages/login/', 'src/pages/logout/'],
  [teams.Goat]: [],
  [teams.Zen]: [
    'src/pages/settings/integration-client-auth/',
    'src/pages/settings/premium-services/',
  ],
  [teams.Lynx]: [
    'src/pages/dashboard/',
    'src/pages/debug/',
    'src/pages/developer-tools/',
    'src/pages/errors/',
    'src/pages/new-design/',
    'src/pages/platform/',
    'src/pages/start/',
    'src/pages/settings/',
  ],
  [teams.Paddington]: ['src/pages/storage/', 'src/pages/lng/'],
} as const satisfies Record<Team, ReadonlyArray<string>>;

/**
 * Paths that are owned by delivery teams (not Lynx by default).
 * These paths are excluded from default Lynx ownership.
 * Files in these paths that don't match any service pattern will return 'Unknown'.
 */
export const deliveryTeamPaths = [
  'angular/src/',
  'public/config.js',
  'src/apis/',
  'src/config/',
  'src/pages/',
  'src/routes.tsx',
  'stories/mocks/',
  'stories/pages/',
  'tests/pages/',
] as const satisfies ReadonlyArray<string>;
